import { useReducer, useCallback } from "react";
import invariant from "invariant";
import ImportRequest from "./model";

// Maximum wait time between import process refresh, in seconds
const MAX_WAIT_TIME = 900;
// Increment of the wait time between import process refresh, in seconds
const WAIT_INCREMENT = 2;

/* State Machine
*  initial -[file-selected]> file-uploading ---------------[file-uploaded]------------------+
*                                  |                                                        |
*                           [create-import-process-wait]---create-import-process-waiting    |
*                                                                      |                    |
*                                                           [create-import-process-ready]   |
*                                                                      |                    |
*       import-process-confirm<------->[import-process-changed]<--->import-process-validating
*                                                  |
*                                      import-process-done
*/
function reduce(state, { type, ...action }) {
    if (state.step === 'initial' && type !== 'file-selected') {
        // Catch dispatched actions coming after cancellation of the import
        return state;
    }

    switch (type) {
    case 'file-selected': {
        invariant(state.step === 'initial', `Bad transition from "${state.step}" to "${type}"`);
        const { file } = action;
        return {
            step: 'file-uploading',
            file,
        };
    }
    case 'file-uploaded': {
        const { key } =  action;
        if (key === null) {
            return {
                step: 'initial',
            };
        }

        invariant(state.step === 'file-uploading', `Bad transition from "${state.step}" to "${type}"`);
        return {
            step: 'import-process-validating',
            file: state.file,
        };
    }
    case 'create-import-process-wait': {
        const { key } =  action;
        invariant(state.step === 'file-uploading', `Bad transition from "${state.step}" to "${type}"`);
        return {
            step: 'create-import-process-waiting',
            key,
            file: state.file,
        };
    }
    case 'create-import-process-ready': {
        invariant(state.step === 'create-import-process-waiting', `Bad transition from "${state.step}" to "${type}"`);
        return {
            step: 'import-process-validating',
            file: state.file,
        };
    }
    case 'import-process-changed': {
        invariant(state.step === 'import-process-validating' || state.step === 'import-process-processing',
            `Bad transition from "${state.step}" to "${type}"`);

        const { importRequest, poll } = action;
        invariant(importRequest, 'import-process-changed requires importRequest payload');

        if (state.step === 'import-process-validating' && !importRequest.isValidating()) {
            return {
                step: 'import-process-confirm',
                file: state.file,
            };
        }

        if (state.step === 'import-process-processing' && importRequest.isDone()) {
            return {
                step: 'import-process-done',
                file: state.file,
            };
        }

        invariant(typeof poll === 'function', 'poll must be a function');
        const wait = state.nextWaitTime || WAIT_INCREMENT;
        const pollingTimeoutID = poll(wait);

        return {
            step: state.step,
            pollingTimeoutID,
            nextWaitTime: Math.min(MAX_WAIT_TIME, wait + WAIT_INCREMENT),
            file: state.file,
        };
    }
    case 'import-process-confirmed': {
        invariant(state.step === 'import-process-confirm', `Bad transition from "${state.step}" to "${type}"`);

        return {
            step: 'import-process-processing',
            file: state.file,
        };
    }
    case 'cancel':
        if (state.pollingTimeoutID) {
            clearTimeout(state.pollingTimeoutID);
        }
        return {
            step: 'initial',
        };
    case 'clear-timeout': {
        const { pollingTimeoutID, ...nextState } = state;
        return nextState;
    }
    default:
        throw new Error(`Unknown type ${type}`);
    }
}

function getInitialState(importRequest) {
    if (!importRequest) {
        return {
            step: 'initial',
        };
    }

    invariant(importRequest instanceof ImportRequest, 'importRequest must be a instance of ImportRequest');
    if (importRequest.isValidating()) {
        return {
            step: 'import-process-validating',
        };
    }
    if (importRequest.isConfirmable()) {
        return {
            step: 'import-process-confirm',
        };
    }
    return {
        step: 'import-process-done',
    };
}

export default function useStateMachine(initial) {
    const [state, dispatch] = useReducer(reduce, initial, getInitialState);

    // keep the instance of transition. dispatch is garanteed to stay accross renders
    const transition = useCallback((type, data) => {
        invariant(type, 'transition is required');
        dispatch({ type, ...data });
    }, [dispatch]);
    return [state, transition];
}
